import type { StateCreator } from 'zustand';
import { CondoStore } from './shared';
import { apiService } from '../../services/api';
import { staticApi } from '../../utils/staticApi';
import { nextRequestToken, isStaleRequest, loadStart, loadSettle } from './shared';

export const createFeesSlice: StateCreator<CondoStore, [], [], Partial<CondoStore>> = (set, get) => ({
  // Fee Management with API integration
  fetchFees: async (condoId?: string) => {
    const token = nextRequestToken('fees');
    set(loadStart('fees')); // 106.1
    try {
      const fees = await apiService.getFees(condoId);
      // 94.4 — discard a response superseded by a newer fetch so a slow
      // condo-A load can't clobber condo-B's fees after a switch.
      if (isStaleRequest(token)) return;
      // A successful (2xx) response — even an empty list — is real data.
      set(state => ({ ...loadSettle('fees', null)(state), fees }));
    } catch (error: any) {
      if (isStaleRequest(token)) return;
      // Sprint 81 (ADR-081) — fetch-error contract. Previously a 403/404 was
      // caught here and rendered as $0.00 with no console trace. Log the
      // endpoint + error and surface the error state; leave `fees` untouched
      // so we distinguish "empty 200 result" from "fetch failed".
      console.error(
        `[condoStore.fetchFees] GET /fees/condo/${condoId ?? '(none)'} failed:`,
        error?.message || error,
      );
      set(loadSettle('fees', error.message || 'Failed to fetch fees'));
    }
  },

  addFee: (fee) =>
    set((state) => ({
      fees: [...state.fees, { ...fee, dueDate: new Date(fee.dueDate) }]
    })),

  createFee: async (feeData) => {
    const { setLoading, setError, fetchFees } = get();
    setLoading(true);
    try {
      await apiService.createFee(feeData);
      await fetchFees(feeData.condoId);
    } catch (error: any) {
      setError(error.message || 'Failed to create fee');
      throw error;
    } finally {
      setLoading(false);
    }
  },

  updateFee: (id, updatedFee) =>
    set((state) => ({
      fees: state.fees.map((fee) =>
        fee.id === id ? { ...fee, ...updatedFee } : fee
      ),
    })),

  updateFeeApi: async (id, feeData) => {
    const { setLoading, setError, fetchFees } = get();
    setLoading(true);
    try {
      await apiService.updateFee(id, feeData);
      await fetchFees(feeData.condoId);
    } catch (error: any) {
      setError(error.message || 'Failed to update fee');
      throw error;
    } finally {
      setLoading(false);
    }
  },

  updateFeeStatus: (id, status) =>
    set((state) => ({
      fees: state.fees.map((fee) =>
        fee.id === id ? {
          ...fee,
          status,
          paidDate: status === 'PAID' ? new Date() : fee.paidDate
        } : fee
      ),
    })),

  deleteFee: (id) =>
    set((state) => ({
      fees: state.fees.filter((fee) => fee.id !== id),
    })),

  deleteFeeApi: async (id) => {
    const { setLoading, setError, fetchFees, fees } = get();
    setLoading(true);
    // Sprint 135 — capture the condo BEFORE the row is gone. `fetchFees()` with
    // no condoId hits the UNSCOPED `GET /api/fees`, which is SUPER_ADMIN-only
    // (ADR-112) and 403s for a condo admin; the Sprint-81 catch then swallows it
    // and leaves `fees` untouched, so the deleted row stayed on screen until a
    // manual reload. Same class as R11-3: calling the unscoped endpoint by
    // accident. Verified live: GET /api/fees → 403, /api/fees/condo/{id} → 200.
    const condoId = fees.find(f => f.id === id)?.condoId;
    try {
      await apiService.deleteFee(id);
      await fetchFees(condoId);
    } catch (error: any) {
      setError(error.message || 'Failed to delete fee');
      throw error;
    } finally {
      setLoading(false);
    }
  },

  // Sprint 135 (R12-2) — delete every per-owner row of a condo-wide assessment.
  deleteFeeGroupApi: async (groupId) => {
    const { setLoading, setError, fetchFees, fees } = get();
    setLoading(true);
    // Same reason as deleteFeeApi above: resolve the condo while the batch's
    // rows are still in the store.
    const condoId = fees.find(f => f.groupId === groupId)?.condoId;
    try {
      await apiService.deleteFeeGroup(groupId);
      await fetchFees(condoId);
    } catch (error: any) {
      setError(error.message || 'Failed to delete assessment');
      throw error;
    } finally {
      setLoading(false);
    }
  },

  // Sprint 134 (R12-1) — `settlement` is optional: the owner Pay-Now path sends
  // nothing and behaves exactly as before; the manager's form sends the owner
  // confirmation + chosen method.
  markFeeAsPaidApi: async (id, settlement) => {
    const { setLoading, setError, fetchFees, fetchPayments } = get();
    setLoading(true);
    try {
      const updated = await apiService.markFeeAsPaid(id, settlement);
      // Update local store immediately, then refresh
      set(state => ({
        fees: state.fees.map(f => f.id === id ? { ...f, status: 'PAID' as const } : f)
      }));
      // Re-fetch to sync with server. Sprint 106 (106.3): the optimistic PAID
      // above can mask server truth if the refetch fails — previously the error
      // was swallowed (`.catch(() => {})`) and the UI claimed PAID with no
      // reload of server state. Now we surface a soft toast AND set the `fees`
      // keyed error so the page can flag "needs refresh", rather than silently
      // swallowing. (We do NOT re-throw — the mark-paid call itself succeeded.)
      // Sprint 122 (122.3 / R9-LOW-1): also refetch PAYMENTS so the Payment
      // History table + "Last Payment" KPI update in place after an in-app
      // mark-paid (previously only fees were refetched, leaving history/KPIs
      // stale until a manual reload). Both refetches share the Sprint-106
      // soft-error contract below — neither is swallowed.
      try {
        await Promise.all([
          fetchFees(updated.condoId),
          fetchPayments(updated.condoId),
        ]);
      } catch (refetchErr: any) {
        console.error('[condoStore.markFeeAsPaidApi] refetch after mark-paid failed:', refetchErr?.message || refetchErr);
        staticApi.message.warning(
          "Payment recorded, but we couldn't refresh the latest balance — reload to confirm.",
        );
        set(loadSettle('fees', "Couldn't refresh fees after payment — reload to confirm."));
      }
    } catch (error: any) {
      setError(error.message || 'Failed to mark fee as paid');
      throw error;
    } finally {
      setLoading(false);
    }
  },

  getOwnerFees: (ownerId) =>
    get().fees.filter(fee => fee.ownerId === ownerId),

  getPendingFees: (condoId?: string) => {
    const { fees } = get();
    const filteredFees = condoId ? fees.filter(fee => fee.condoId === condoId) : fees;
    return filteredFees.filter(fee => fee.status === 'PENDING');
  },

  getOverdueFees: (condoId?: string) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const { fees } = get();
    const filteredFees = condoId ? fees.filter(fee => fee.condoId === condoId) : fees;

    return filteredFees.filter(fee => {
      // Already-OVERDUE fees are overdue regardless of due date.
      if (fee.status === 'OVERDUE') return true;
      // PENDING fees count as overdue once past their due date.
      if (fee.status !== 'PENDING') return false;
      const dueDate = new Date(fee.dueDate);
      dueDate.setHours(0, 0, 0, 0);
      return dueDate < today;
    });
  },

  // Total unpaid dues for a condo = sum of fees still owing (PENDING or
  // OVERDUE), excluding PAID and WAIVED.
  getOutstandingTotal: (condoId?: string) => {
    const { fees } = get();
    const filteredFees = condoId ? fees.filter(fee => fee.condoId === condoId) : fees;
    return filteredFees
      .filter(fee => fee.status === 'PENDING' || fee.status === 'OVERDUE')
      .reduce((sum, fee) => sum + fee.amount, 0);
  },
});
